import { Component, HostListener, ChangeDetectionStrategy, inject } from '@angular/core';
import { Observable, combineLatest, map, shareReplay } from 'rxjs';
import { Contract } from '@shared/entities/contract';
import { Machine } from '@shared/entities/machine';
import { MachineFilter } from '@shared/entities/machineFilter';
import { Paginate } from '@shared/entities/paginate';
import { FilterSelectedEventType } from '@shared/models/types';
import { ContractAlertService } from '@shared/services/api/contracts-alert.service';
import { MachineAlertService } from '@shared/services/api/machine-alert.service';
import { MachineFiltersService } from '@shared/services/api/machine-filters.service';
import { MachineFilterURL, MachineService } from '@shared/services/api/machine.service';

@Component({
  selector: 'app-homepage',
  templateUrl: './homepage.component.html',
  styleUrls: ['./homepage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomepageComponent {
  private _machineService = inject(MachineService);
  private _machineFilterService = inject(MachineFiltersService);
  private _machineAlertService = inject(MachineAlertService);
  private _contractAlertService = inject(ContractAlertService);

  private readonly paginatedMachines$: Observable<Paginate<Machine>> = this._machineService.all$.pipe(shareReplay(1));

  machines$: Observable<Machine[]> = this.paginatedMachines$.pipe(map((p) => p.data));
  prevPage$: Observable<number | undefined> = this.paginatedMachines$.pipe(map((p) => p.prevPage));
  currentPage$: Observable<number | undefined> = this.paginatedMachines$.pipe(map((p) => p.currentPage));
  nextPage$: Observable<number | undefined> = this.paginatedMachines$.pipe(map((p) => p.nextPage));
  totalPages$: Observable<number | undefined> = this.paginatedMachines$.pipe(map((p) => p.totalPage));

  machineFilters$: Observable<MachineFilter> = this._machineFilterService.all$;
  machinesBroken$: Observable<Machine[]> = this._machineAlertService.broken$;
  contractsLapsed$: Observable<Contract[]> = this._contractAlertService.lapsed$;

  alerts$: Observable<boolean> = combineLatest([this.machinesBroken$, this.contractsLapsed$]).pipe(
    map(([machines, contracts]) => machines.length + contracts.length > 0)
  );

  isOpen = false;
  isMobile = window.innerWidth < 768;

  @HostListener('window:resize')
  onResize() {
    this.isMobile = window.innerWidth < 768;
  }

  getYear() {
    return new Date().getFullYear();
  }

  onSelectedFilter(filters: FilterSelectedEventType[]) {
    const filtersName = filters.reduce((acc: any, filter: FilterSelectedEventType) => {
      acc[filter.name] = filter.selectedValue;
      return acc;
    }, {});
    this._machineFilterService.filters = filtersName as MachineFilterURL;
    this._machineService.filters = filtersName as MachineFilterURL;
  }

  onChangePage(page: number) {
    this._machineService.refresh(page);
  }
}
